// Portions of this file are Copyright 2021 Google LLC, and licensed under GPL2+. See COPYING.

import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { PrimeReactProvider } from 'primereact/api';
import HomePage from './pages/HomePage.tsx';
import { registerCustomAppHeightCSSProperty } from './utils.ts';

import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.min.css";
import './index.css';
import './pages/pages.css';

import debug from 'debug';

const log = debug('app:log');

if (import.meta.env.DEV) {
  debug.enable('*');
  log('Logging is enabled!');
} else {
  debug.disable();
}

// Lazily loaded so the homepage does not download the workspace bundles.
const WorkspacePage = lazy(() => import('./pages/WorkspacePage.tsx'));
const AdminPage = lazy(() => import('./pages/AdminPage.tsx'));

registerCustomAppHeightCSSProperty();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <PrimeReactProvider value={{ ripple: true }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/studio" element={
            <Suspense fallback={
              <div className="route-status" role="status">
                <span className="route-status-icon"><i className="pi pi-spinner pi-spin" /></span>
                <strong>Loading workspace</strong>
              </div>
            }>
              <WorkspacePage />
            </Suspense>
          } />
          <Route path="/admin" element={
            <Suspense fallback={
              <div className="route-status" role="status">
                <span className="route-status-icon"><i className="pi pi-spinner pi-spin" /></span>
                <strong>Loading admin</strong>
              </div>
            }>
              <AdminPage />
            </Suspense>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </PrimeReactProvider>
  </React.StrictMode>
);
